<template>
    <div class="container py-4 nch--wiki-show">
        <div v-if="loading" class="text-center text-muted py-5"><i class="fas fa-circle-notch fa-spin fa-2x"></i></div>
        <div v-else-if="error" class="alert alert-warning">{{ error }}</div>
        <template v-else>
            <!-- Cabeçalho -->
            <div class="card shadow-sm mb-3">
                <div class="card-body">
                    <div class="d-flex align-items-start flex-wrap">
                        <div class="flex-grow-1 min-w-0 mr-3">
                            <h1 class="h3 mb-1">{{ wiki.title }}</h1>
                            <div class="text-muted small nch--relation">
                                {{ relationText }}<template v-if="wiki.classroom"> · {{ $t('wiki.classroom') }} {{ wiki.classroom }}</template>
                            </div>
                        </div>
                        <div class="mt-1">
                            <span class="badge badge-pill mr-1 nch--status" :class="statusClass"><i :class="statusIcon" class="mr-1"></i>{{ statusText }}</span>
                            <span v-if="wiki.status !== 'not_started'" class="badge badge-pill" :class="deadlineClass"><i class="far fa-calendar-alt mr-1"></i>{{ deadlineText }}</span>
                        </div>
                    </div>
                    <div v-if="wiki.description" class="mt-3">
                        <div class="nch--wiki-html nch--description" :class="{ 'nch--description__clamp': !descriptionOpen }" v-html="wiki.description"></div>
                        <a href="#" class="small" :style="{ color: color.primary }" @click.prevent="descriptionOpen = !descriptionOpen">
                            <i class="fas mr-1" :class="descriptionOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>{{ descriptionOpen ? $t('wiki.read_less') : $t('wiki.read_more') }}
                        </a>
                    </div>
                    <div class="d-flex flex-wrap small text-muted mt-3 nch--indicators">
                        <span class="mr-4"><i class="far fa-user mr-1"></i><strong>{{ wiki.participants_count }}</strong> {{ $t('wiki.participants') }}</span>
                        <span class="mr-4"><i class="fas fa-pen mr-1"></i><strong>{{ wiki.contributions_count }}</strong> {{ $t('wiki.contributions') }}</span>
                        <span class="mr-4"><i class="far fa-clock mr-1"></i>{{ $t('wiki.last_update') }}: <strong>{{ date(wiki.last_activity_at) }}</strong></span>
                    </div>
                </div>
            </div>

            <div v-if="!wiki.can_edit && closedReason" class="alert py-2 nch--closed-notice" :class="wiki.status === 'not_started' ? 'alert-info' : 'alert-secondary'">
                <i class="fas mr-1" :class="wiki.status === 'not_started' ? 'fa-clock' : 'fa-lock'"></i>{{ closedReason }}
            </div>

            <!-- 3 colunas -->
            <div class="row">
                <div class="col-lg-3 mb-3">
                    <div class="card shadow-sm">
                        <div class="card-body">
                            <div class="d-flex align-items-center mb-2">
                                <strong>{{ $t('wiki.pages') }}</strong>
                                <button v-if="wiki.can_create_pages" type="button" class="btn btn-sm btn-outline-primary ml-auto" @click="openNewPage()"><i class="fas fa-plus mr-1"></i>{{ $t('wiki.new_page') }}</button>
                            </div>
                            <div class="input-group input-group-sm mb-2">
                                <div class="input-group-prepend"><span class="input-group-text bg-white"><i class="fas fa-search text-muted"></i></span></div>
                                <input v-model.trim="search" type="search" class="form-control nch--page-search" :placeholder="$t('wiki.search_page')" />
                            </div>
                            <div v-if="!wiki.pages.length" class="text-muted small">{{ $t('wiki.no_pages') }}</div>
                            <template v-else-if="search">
                                <div v-if="!filteredPages.length" class="text-muted small">{{ $t('wiki.no_results') }}</div>
                                <wiki-page-tree :pages="filteredPages" :selected-id="page ? page.id : null" :color="color" @select="openPage"></wiki-page-tree>
                            </template>
                            <wiki-page-tree v-else :pages="wiki.pages" :selected-id="page ? page.id : null" :color="color" @select="openPage"></wiki-page-tree>
                        </div>
                    </div>
                </div>

                <div class="col-lg-6 mb-3">
                    <div class="card shadow-sm">
                        <div class="card-body">
                            <div v-if="!page" class="text-muted text-center py-5">{{ $t('wiki.select_page') }}</div>
                            <template v-else>
                                <ul class="nav nav-pills nch--wiki-tabs mb-3">
                                    <li v-for="t in tabs" :key="t.key" class="nav-item">
                                        <a href="#" class="nav-link" :class="{ active: tab === t.key, disabled: t.disabled }" :style="tab === t.key ? { backgroundColor: color.primary, color: '#fff' } : { color: '#525f7f' }" @click.prevent="!t.disabled && switchTab(t.key)">
                                            <i :class="t.icon" class="mr-1"></i>{{ $t(`wiki.tabs.${t.key}`) }}
                                        </a>
                                    </li>
                                </ul>
                                <div class="d-flex align-items-center flex-wrap mb-2">
                                    <h2 class="h4 mb-0 mr-2">{{ page.title }}</h2>
                                    <span v-if="page.locked" class="badge badge-pill badge-warning"><i class="fas fa-lock mr-1"></i>{{ $t('wiki.locked_short') }}</span>
                                </div>
                                <div v-if="page.locked" class="alert alert-warning py-2 small">{{ $t('wiki.page_locked') }}</div>

                                <div v-if="tab === 'read'">
                                    <div class="nch--wiki-html" @click="onContentClick" v-html="page.content"></div>
                                    <hr />
                                    <div class="small text-muted">{{ $t('wiki.last_edit', { user: page.last_editor || '—', date: date(page.updated_at) }) }}</div>
                                </div>
                                <wiki-editor v-else-if="tab === 'edit'" :key="`edit-${page.id}`" ref="editor" :wiki-id="wikiId" :page="page" @saved="onSaved" @dirty="editorDirty = $event"></wiki-editor>
                                <wiki-history v-else-if="tab === 'history'" :wiki-id="wikiId" :page-id="page.id"></wiki-history>
                                <wiki-discussion v-else :wiki-id="wikiId" :page-id="page.id" :can-post="wiki.can_edit" @published="reloadWiki"></wiki-discussion>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="col-lg-3 mb-3">
                    <div class="card shadow-sm">
                        <div class="card-body">
                            <strong class="d-block mb-2"><i class="fas fa-history mr-1"></i>{{ $t('wiki.feed.title') }}</strong>
                            <div v-if="!wiki.feed.length" class="text-muted small">{{ $t('wiki.feed.empty') }}</div>
                            <div v-for="(f, i) in wiki.feed" :key="i" class="nch--feed-item small py-2">
                                <strong>{{ f.user || '—' }}</strong> — <a href="#" :style="{ color: color.primary }" @click.prevent="openPageById(f.page_id)">{{ feedText(f) }}</a>
                                <div class="text-muted">{{ date(f.at) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Nova página -->
            <div v-if="newPage.open" class="nch--modal-backdrop" @click.self="newPage.open = false">
                <div class="card shadow nch--modal">
                    <div class="card-body">
                        <h5 class="mb-3">{{ $t('wiki.new_page') }}</h5>
                        <div class="form-group">
                            <label class="small font-weight-bold">{{ $t('wiki.new_page_title') }} *</label>
                            <input v-model.trim="newPage.title" type="text" class="form-control" maxlength="191" @keyup.enter="createPage" />
                        </div>
                        <div class="form-group">
                            <label class="small font-weight-bold">{{ $t('wiki.parent_page') }}</label>
                            <select v-model="newPage.parent_id" class="custom-select">
                                <option :value="null">{{ $t('wiki.no_parent') }}</option>
                                <option v-for="p in flatPages" :key="p.id" :value="p.id">{{ p.label }}</option>
                            </select>
                        </div>
                        <div class="d-flex justify-content-end">
                            <button type="button" class="btn btn-sm btn-outline-secondary mr-2" @click="newPage.open = false">{{ $t('wiki.cancel') }}</button>
                            <button type="button" class="btn btn-sm btn-primary" :style="{ backgroundColor: color.primary, borderColor: color.primary }" :disabled="!newPage.title || newPage.saving" @click="createPage">{{ $t('wiki.create') }}</button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import WikiPageTree from './WikiPageTree';
import WikiEditor from './WikiEditor';
import WikiHistory from './WikiHistory';
import WikiDiscussion from './WikiDiscussion';
import { deadlineLabel, friendlyDate, formatDate, formatTime, relationLabel, statusLabel } from './wikiHelpers';

/**
 * Tela da wiki na área do aluno (backlog#343): cabeçalho com vínculo,
 * situação (inclusive "Abre em…"), prazo, descrição e indicadores; árvore de
 * páginas com busca e nova página; abas Ler / Editar / Histórico / Discussão
 * (sair de Editar com texto não salvo pede confirmação); últimas alterações.
 */
export default {
    name: 'WikiShow',

    components: { WikiPageTree, WikiEditor, WikiHistory, WikiDiscussion },

    props: { wikiId: { type: Number, required: true } },

    data: () => ({
        wiki: null,
        page: null,
        tab: 'read',
        search: '',
        loading: true,
        error: null,
        descriptionOpen: false,
        editorDirty: false,
        newPage: { open: false, title: '', parent_id: null, saving: false },
    }),

    computed: {
        color() { return this.$store.getters.COLOR || {}; },
        tabs() {
            const canEdit = this.page && this.page.can_edit;
            return [
                { key: 'read', icon: 'fas fa-book-open' },
                { key: 'edit', icon: 'fas fa-pen', disabled: !canEdit },
                { key: 'history', icon: 'fas fa-history' },
                { key: 'discussion', icon: 'far fa-comments' },
            ];
        },
        relationText() { return relationLabel(this.wiki, this.$t.bind(this)); },
        statusText() { return statusLabel(this.wiki, this.$t.bind(this)); },
        statusClass() { return { editing: 'badge-info', closed: 'badge-light border text-muted', not_started: 'badge-light border text-muted' }[this.wiki.status] || 'badge-light'; },
        statusIcon() { return { editing: 'fas fa-pen', closed: 'fas fa-lock', not_started: 'far fa-clock' }[this.wiki.status] || ''; },
        deadlineText() {
            const d = deadlineLabel(this.wiki);
            if (!d) return this.$t('wiki.deadline.none');
            return d.key === 'ends_in' ? this.$tc('wiki.deadline.ends_in', d.days, { days: d.days }) : this.$t(`wiki.deadline.${d.key}`, d.params || {});
        },
        deadlineClass() {
            const d = deadlineLabel(this.wiki);
            if (!d || d.key === 'over' || d.key === 'not_started') return 'badge-light border text-muted';
            return d.days > 3 ? 'badge-light border' : 'badge-warning';
        },
        closedReason() {
            if (this.wiki.status === 'not_started') return this.$t('wiki.not_started_notice', { date: formatDate(this.wiki.start_at), time: formatTime(this.wiki.start_at) });
            if (this.wiki.status !== 'editing') return this.$t('wiki.closed_notice');
            return null;
        },
        flatPages() {
            const out = [];
            const walk = (list, depth) => list.forEach((p) => { out.push({ id: p.id, label: `${'— '.repeat(depth)}${p.title}` }); walk(p.children || [], depth + 1); });
            walk(this.wiki ? this.wiki.pages : [], 0);
            return out;
        },
        filteredPages() {
            const q = this.search.toLowerCase();
            const out = [];
            const walk = (list) => list.forEach((p) => { if (p.title.toLowerCase().includes(q)) out.push({ ...p, children: [] }); walk(p.children || []); });
            walk(this.wiki.pages);
            return out;
        },
    },

    watch: {
        tab(value) {
            if (this.page) window.history.replaceState(null, '', `?page=${this.page.slug}&tab=${value}`);
        },
    },

    created() {
        this.load();
        window.addEventListener('beforeunload', this.onBeforeUnload);
    },

    beforeUnmount() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
    },

    methods: {
        date(v) { return friendlyDate(v, this.$t.bind(this)); },

        feedText(f) {
            const key = f.type === 'created' ? 'wiki.feed.created' : (f.type === 'edited' ? 'wiki.feed.edited' : 'wiki.feed.commented');
            return this.$t(key, { page: f.page_title, summary: f.summary });
        },

        /** Texto não salvo na aba Editar: confirma antes de trocar de aba/página. */
        confirmLeaveEditor() {
            if (this.tab !== 'edit' || !this.editorDirty) return true;
            if (window.confirm(this.$t('wiki.editor.unsaved'))) { this.editorDirty = false; return true; }
            return false;
        },

        onBeforeUnload(e) {
            if (this.tab === 'edit' && this.editorDirty) { e.preventDefault(); e.returnValue = ''; }
        },

        switchTab(key) {
            if (key === this.tab) return;
            if (!this.confirmLeaveEditor()) return;
            this.tab = key;
        },

        load() {
            this.loading = true;
            return window.axios.get(`/api/app/wikis/${this.wikiId}`)
                .then(({ data }) => {
                    this.wiki = data;
                    const params = new URLSearchParams(window.location.search);
                    const wanted = this.findPage((p) => p.slug === params.get('page')) || this.flat(this.wiki.pages)[0];
                    if (wanted) this.openPage(wanted, params.get('tab'), true);
                })
                .catch((e) => { const res = (e && e.response) || {}; this.error = (res.data && (res.data.error || res.data.message)) || this.$t('wiki.error'); })
                .finally(() => { this.loading = false; });
        },

        /** Recarrega árvore, indicadores e feed sem perder a página aberta. */
        reloadWiki() {
            return window.axios.get(`/api/app/wikis/${this.wikiId}`).then(({ data }) => { this.wiki = data; }).catch(() => {});
        },

        flat(list) {
            const out = [];
            const walk = (l) => l.forEach((p) => { out.push(p); walk(p.children || []); });
            walk(list);
            return out;
        },

        findPage(predicate) { return this.flat(this.wiki.pages).find(predicate) || null; },

        openPage(p, tab = null, force = false) {
            if (!force && this.page && p.id !== this.page.id && !this.confirmLeaveEditor()) return;
            window.axios.get(`/api/app/wikis/${this.wikiId}/pages/${p.id}`)
                .then(({ data }) => {
                    this.page = data;
                    this.editorDirty = false;
                    this.tab = tab && (tab !== 'edit' || data.can_edit) ? tab : 'read';
                    window.history.replaceState(null, '', `?page=${data.slug}&tab=${this.tab}`);
                })
                .catch(() => {});
        },

        openPageById(id) {
            const p = this.findPage((x) => x.id === id);
            if (p) this.openPage(p);
        },

        /** Links [[Página]] para páginas existentes abrem a página. */
        onContentClick(e) {
            const link = e.target.closest('a.nch--wiki-link');
            if (!link) return;
            e.preventDefault();
            if (link.dataset.pageId) this.openPageById(Number(link.dataset.pageId));
        },

        openNewPage(title = '', parentId = null) {
            this.newPage = { open: true, title, parent_id: parentId || (this.page ? this.page.id : null), saving: false };
        },

        async createPage() {
            if (!this.newPage.title) return;
            if (!this.confirmLeaveEditor()) return;
            this.newPage.saving = true;
            try {
                const { data } = await window.axios.post(`/api/app/wikis/${this.wikiId}/pages`, { title: this.newPage.title, parent_id: this.newPage.parent_id });
                this.newPage.open = false;
                this.$notify({ type: 'success', message: this.$t('wiki.page_created') });
                await this.reloadWiki();
                this.page = data.page;
                this.editorDirty = false;
                this.tab = data.page.can_edit ? 'edit' : 'read';
            } catch (e) {
                const res = (e && e.response) || {};
                this.$notify({ type: 'danger', message: (res.data && (res.data.error || res.data.message)) || this.$t('wiki.error') });
            } finally {
                this.newPage.saving = false;
            }
        },

        async onSaved(page) {
            this.page = page;
            this.editorDirty = false;
            this.tab = 'read';
            await this.reloadWiki();
        },
    },
};
</script>

<style>
.nch--wiki-show .nch--wiki-html img { max-width: 100%; }
.nch--wiki-show .nch--wiki-html table { border-collapse: collapse; width: 100%; margin-bottom: 12px; }
.nch--wiki-show .nch--wiki-html td, .nch--wiki-show .nch--wiki-html th { border: 1px solid #dee2e6; padding: 6px 8px; }
.nch--wiki-show .nch--wiki-html p:last-child { margin-bottom: 0; }
/* cartão de arquivo (PDF, PPTX, DOCX, XLSX) */
.nch--wiki-show .nch--wiki-html a.nch--wiki-file { display: flex; align-items: center; gap: 12px; max-width: 520px; padding: 10px 14px; margin: 8px 0; border: 1px solid #e0e4ea; border-radius: 10px; background: #f8f9fc; color: #32325d !important; text-decoration: none !important; font-weight: 600; }
.nch--wiki-show .nch--wiki-html a.nch--wiki-file:hover { background: #eef1f8; }
.nch--wiki-show .nch--wiki-html a.nch--wiki-file::before { content: attr(data-kind); flex: 0 0 auto; min-width: 44px; padding: 6px 8px; border-radius: 8px; background: #5e72e4; color: #fff; font-size: 11px; font-weight: 700; text-align: center; letter-spacing: 0.3px; }
.nch--wiki-show .nch--wiki-html a.nch--wiki-file::after { content: attr(data-size) " · " attr(data-download); margin-left: auto; font-size: 12px; font-weight: 500; color: #8898aa; white-space: nowrap; }
.nch--description__clamp { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.nch--wiki-tabs .nav-link { padding: 6px 12px; font-size: 14px; border-radius: 20px; }
.nch--wiki-tabs .nav-link.disabled { opacity: 0.45; }
.nch--feed-item + .nch--feed-item { border-top: 1px solid #f1f1f1; }
.nch--wiki-link { border-bottom: 1px dashed currentColor; }
.nch--modal-backdrop { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.4); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 16px; }
.nch--modal { width: 100%; max-width: 480px; }
.min-w-0 { min-width: 0; }
</style>
